import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useTranslation } from "react-i18next";
import { useAIToggle } from "../hooks/useAIToggle";

interface JessToggleButtonProps {
  isDarkTheme?: boolean;
}

const BUTTON_SIZE = 44;
const ICON_SIZE = 22;
const BORDER_RADIUS = "12px";

const JessToggleButton: React.FC<JessToggleButtonProps> = ({
  isDarkTheme = false,
}) => {
  const { t } = useTranslation();
  const { isAIEnabled, toggleAI, loading } = useAIToggle();

  const getButtonStyles = (): SxProps<Theme> => {
    if (isAIEnabled) {
      if (isDarkTheme) {
        return {
          background: "rgba(121, 86, 247, 0.2)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(121, 86, 247, 0.45)",
          borderRadius: BORDER_RADIUS,
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          boxShadow: "0 0 14px rgba(121, 86, 247, 0.35)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background:
              "radial-gradient(circle at 50% 0%, rgba(121,86,247,0.3) 0%, transparent 70%)",
            pointerEvents: "none",
          },
          "&:hover": {
            background: "rgba(121, 86, 247, 0.35)",
            borderColor: "rgba(121, 86, 247, 0.7)",
            transform: "translateY(-2px) scale(1.05)",
            boxShadow: "0 8px 28px rgba(121, 86, 247, 0.5)",
          },
          "&:active": { transform: "translateY(-1px) scale(1.02)" },
        };
      }
      return {
        background: "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)",
        border: "none",
        borderRadius: BORDER_RADIUS,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        boxShadow:
          "0 4px 14px rgba(121, 86, 247, 0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background:
            "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.2) 0%, transparent 60%)",
          pointerEvents: "none",
        },
        "&:hover": {
          transform: "translateY(-2px) scale(1.05)",
          boxShadow: "0 8px 24px rgba(121, 86, 247, 0.6)",
        },
        "&:active": { transform: "translateY(-1px) scale(1.02)" },
      };
    }

    // Disabled (Jess OFF)
    if (isDarkTheme) {
      return {
        background: "rgba(255, 255, 255, 0.06)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: BORDER_RADIUS,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          background: "rgba(255, 255, 255, 0.12)",
          borderColor: "rgba(255, 255, 255, 0.18)",
          transform: "translateY(-2px) scale(1.05)",
        },
        "&:active": { transform: "translateY(-1px) scale(1.02)" },
      };
    }
    return {
      background: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: "1px solid rgba(0, 0, 0, 0.08)",
      borderRadius: BORDER_RADIUS,
      width: BUTTON_SIZE,
      height: BUTTON_SIZE,
      boxShadow:
        "0 2px 8px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.6)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        borderColor: "rgba(121, 86, 247, 0.25)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
        transform: "translateY(-2px) scale(1.05)",
      },
      "&:active": { transform: "translateY(-1px) scale(1.02)" },
    };
  };

  const getIconStyles = (): SxProps<Theme> => {
    if (isAIEnabled) {
      return {
        fontSize: ICON_SIZE,
        color: isDarkTheme ? "#a78bfa" : "#ffffff",
        filter: isDarkTheme
          ? "drop-shadow(0 0 5px rgba(121,86,247,0.7))"
          : "drop-shadow(0 1px 3px rgba(0,0,0,0.25))",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        zIndex: 1,
      };
    }
    return {
      fontSize: ICON_SIZE,
      color: isDarkTheme ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      zIndex: 1,
    };
  };

  return (
    <Tooltip
      title={
        isAIEnabled
          ? t("ai_enabled") || "Jess ON"
          : t("ai_disabled") || "Jess OFF"
      }
      arrow
    >
      <IconButton
        onClick={toggleAI}
        disabled={loading}
        disableRipple
        sx={getButtonStyles()}
      >
        <SupportAgentIcon sx={getIconStyles()} />
      </IconButton>
    </Tooltip>
  );
};

export default JessToggleButton;
